import React, { useCallback } from 'react';
import { Filter } from 'lucide-react';
import { Form, FormField } from 'components/ui/form';
import { cn } from 'lib/utils';
import { useForm } from 'react-hook-form';
import { InputGs, SelectGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import {
  useFilterStore,
  IFilterFields,
  usePaginationStore,
} from 'store/hub/hubVehicle';
import { useHubCarrier, useHubVehicle } from 'hooks/api';
import { useQuery } from 'react-query';

interface DataFilterProps {
  className?: string;
}

export const DataFilter: React.FC<DataFilterProps> = ({ className }) => {
  const [isOpened, setIsOpened] = React.useState<boolean>(false);
  const { filtersData, setFiltersData, clearFilters } = useFilterStore();
  const { pagination, setPagination } = usePaginationStore();
  const {
    listVehicleType,
    listVehicleBrand,
    listVehicleModel,
    listVehicleSubtype,
  } = useHubVehicle();
  const { fetchCarriers } = useHubCarrier();
  const [selectedVehicleType, setSelectedVehicleType] = React.useState<
    string | null
  >(null);
  const [selectedBrand, setSelectedBrand] = React.useState<string>('');
  const form = useForm({
    defaultValues: filtersData,
  });

  const onFilter = (fields: IFilterFields) => {
    setPagination({ ...pagination, page: 1 });
    setFiltersData({ ...fields });
  };

  const handleClear = React.useCallback(() => {
    form.reset();
    clearFilters();
    setIsOpened(false);
  }, [clearFilters, form]);

  const { data: dataVehicleTypes, isLoading: isLoadingVehicleTypes } = useQuery(
    {
      queryKey: ['listVehicleType'],
      queryFn: () => listVehicleType({}),
      refetchOnWindowFocus: false,
    }
  );
  const { data: dataVehicleSubtypes, isLoading: isLoadingVehicleSubtypes } =
    useQuery({
      queryKey: ['listVehicleSubtype', selectedVehicleType],
      queryFn: () =>
        selectedVehicleType
          ? listVehicleSubtype({ vehicle_type_id: selectedVehicleType })
          : Promise.resolve([]),
      enabled: !!selectedVehicleType,
      refetchOnWindowFocus: false,
    });

  const { data: dataVehicleBrand, isLoading: isLoadingVehicleBrand } = useQuery(
    {
      queryKey: ['listVehicleBrand'],
      queryFn: () => listVehicleBrand({}),
      refetchOnWindowFocus: false,
    }
  );

  const { data: dataModel, isLoading: isLoadingModel } = useQuery({
    queryKey: ['listVehicleModel', selectedBrand],
    queryFn: () => listVehicleModel({ vehicle_brand_id: selectedBrand }),
    refetchOnWindowFocus: false,
  });

  const { data: carriers, isLoading: loadinDocTypes } = useQuery({
    queryKey: ['fetchCarriers'],
    queryFn: () => fetchCarriers(),
    staleTime: 1000 * 60 * 5, // 5 min,
    refetchOnWindowFocus: false,
  });

  const handleTypeVehicleChange = useCallback(
    (vehicle_type_id: string) => {
      form.setValue('vehicle_subtype_id', '');
      setSelectedVehicleType(() => vehicle_type_id);
    },
    [form]
  );

  const handleBrandChange = useCallback(
    (brand_id: string) => {
      form.setValue('vehicle_model_id', '');
      setSelectedBrand(() => brand_id);
    },
    [form]
  );

  return (
    <div
      className={cn(
        'flex flex-col w-max rounded-md px-6 py-4 bg-white shadow-sm shadow-slate-300 duration-300 transition-all',
        className,
        isOpened && 'w-full'
      )}
    >
      <button
        onClick={() => {
          setIsOpened(!isOpened);
        }}
        className="group flex gap-2 items-center duration-300 transition-all"
      >
        <Filter
          className={cn(
            'cursor-pointer group-hover:fill-black transition-all duration-300',
            isOpened && 'fill-black'
          )}
          size={14}
        />
        <span className="text-slate-600 text-sm">Filtrar</span>
      </button>
      {isOpened && (
        <div id="filterForm" className="border-t-slate-300 border-t pt-3 mt-4 ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onFilter)}>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <FormField
                  name="carrier_id"
                  control={form.control}
                  render={({ field }) => (
                    <SelectGs
                      {...field}
                      label={'Transportadora'}
                      onValueChange={(value: string) => {
                        field.onChange(value);
                      }}
                      content={carriers?.comboOptions || []}
                      isLoading={loadinDocTypes}
                      value={field.value ?? undefined}
                    />
                  )}
                />
                <FormField
                  name="vehicle_plate"
                  control={form.control}
                  render={({ field }) => (
                    <InputGs {...field} label="Placa do Veiculo" />
                  )}
                />
                <FormField
                  name="renavam"
                  control={form.control}
                  render={({ field }) => <InputGs {...field} label="Renavam" />}
                />
                <FormField
                  name="vehicle_type_id"
                  control={form.control}
                  render={({ field }) => (
                    <SelectGs
                      {...field}
                      label={'Tipo de Veiculo'}
                      onValueChange={(value: string) => {
                        field.onChange(value);
                        handleTypeVehicleChange(value);
                      }}
                      content={dataVehicleTypes || []}
                      isLoading={isLoadingVehicleTypes}
                      value={field.value ?? undefined}
                    />
                  )}
                />
                <FormField
                  name="vehicle_subtype_id"
                  control={form.control}
                  render={({ field }) => (
                    <SelectGs
                      {...field}
                      label={'Subtipo'}
                      onValueChange={(value: string) => {
                        field.onChange(value);
                      }}
                      content={dataVehicleSubtypes || []}
                      isLoading={isLoadingVehicleSubtypes}
                      value={field.value ?? undefined}
                      disabled={!selectedVehicleType}
                    />
                  )}
                />

                <FormField
                  name="vehicle_brand_id"
                  control={form.control}
                  render={({ field }) => (
                    <SelectGs
                      {...field}
                      label={'Marca do Veiculo'}
                      onValueChange={(value: string) => {
                        field.onChange(value);
                        handleBrandChange(value);
                      }}
                      content={dataVehicleBrand || []}
                      isLoading={isLoadingVehicleBrand}
                      value={field.value ?? undefined}
                    />
                  )}
                />

                <FormField
                  name="vehicle_model_id"
                  control={form.control}
                  render={({ field }) => (
                    <SelectGs
                      {...field}
                      label={'Modelo do Veiculo'}
                      onValueChange={(value: string) => {
                        field.onChange(value);
                      }}
                      content={dataModel || []}
                      isLoading={isLoadingModel}
                      value={field.value ?? undefined}
                    />
                  )}
                />
              </div>

              <div className="flex justify-end w-full gap-4">
                <Button.Root
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={handleClear}
                >
                  Limpar filtro
                </Button.Root>
                <Button.Root
                  variant="primary"
                  size="sm"
                  type="submit"
                  disabled={false}
                >
                  Buscar
                </Button.Root>
              </div>
            </form>
          </Form>
        </div>
      )}
    </div>
  );
};
